<script setup>
import {useRouter} from "nuxt/app"
import CartItem from '~/components/partials/CartItem'
import {toMoney} from '~/utils/toMoney'
import {useCartStore} from "~/store/cart"
import {useAuthStore} from "~/store/auth"
import {useNotification} from "~/composables/useNotification"
import BuySteps from "~/components/common/BuySteps.vue"
import Button from "~/components/common/Button.vue"

const cartStore = useCartStore()
const authStore = useAuthStore()
const notification = useNotification()

const router = useRouter()
const config = useRuntimeConfig()
const cartItems = computed(() => cartStore.items)
const cartTotal = computed(() => cartStore.total)
const cartTaxes = computed(() => cartStore.taxes)
const cartSubtotal = computed(() => cartStore.subtotal)
const isEmptyCart = computed(() => !cartStore.hasItems)
const isAuth = computed(() => authStore.isAuth)
const breadcrumbs = ref([
  {label: 'Inicio', link: '/'},
  {label: 'Cesta de la Compra', link: '/compra'}
])

const goNextStep = async () => {
  if (isEmptyCart.value) {
    return notification.showSecondary('Carrito de la compra', 'Añade al menos un producto al carrito de la compra para continuar.')
  }

  if (!isAuth.value) {
    await router.push('/compra/iniciar-sesion')
    return
  }

  await router.push('/compra/envio-y-facturacion')
}

useSEOData('buy-cart')
</script>

<template>
  <div class="grid grid-cols-12 buy-index">
    <div class="col-span-12">
      <h1 class="text-2xl lg:text-4xl font-bold italic px-4 lg:px-8 underline text-[#363330] mt-5">Cesta de la Compra</h1>
      <BuySteps class="my-3" step="1"/>
    </div>

    <template v-if="!isEmptyCart">
      <div class="col-span-12 lg:col-span-8 grid grid-cols-12 px-4 lg:px-8">

        <div class="col-span-12 mt-5">
          <h2 class="text-[1rem] italic font-bold underline uppercase mb-2">Artículos</h2>
        </div>

        <CartItem
          v-for="(item, index) in cartItems"
          :key="index"
          :item="item"
          :to-money="toMoney"
        />
      </div>

      <div class="col-span-12 lg:col-span-4 grid grid-cols-12 mt-3 lg:mt-0 px-4 lg:px-8 bg-white items-center">
        <div class="col-span-12 bg-red-800 text-white text-center py-8">
          <h2 class="text-md underline mb-3 uppercase italic">Importe</h2>
          <p class="text-md italic mb-3">Subtotal: {{ toMoney(cartSubtotal) }}</p>
          <p class="text-md italic mb-3">IVA: {{ toMoney(cartTaxes) }}</p>
          <p class="text-4xl font-bold italic uppercase">Total: {{ toMoney(cartTotal) }}</p>
        </div>
      </div>
    </template>
    <div
      v-if="isEmptyCart"
      class="col-span-12 flex justify-center items-center min-h-[400px] uppercase"
    >
      Tu carrito está vacío
    </div>

    <div class="col-span-12 flex justify-center lg:justify-between px-4 lg:px-8 my-12 gap-10">
      <Button
        severity="tertiary"
        icon="pi-backward"
        label="Volver"
        to="/"
      />
      <Button
        severity="primary"
        icon="pi-forward"
        label="Siguiente"
        @click="goNextStep"
        />
    </div>
  </div>
</template>
